/**
 * TanStack Query hooks for Company Info
 */

import type { CompanyInfoPublic, UpdateCompanyInfoRequest } from '@garden-platform/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { companyInfoApi } from '../services/organizationSettingsApi';

export const companyInfoQueryKeys = {
  all: ['companyInfo'] as const,
  detail: () => [...companyInfoQueryKeys.all, 'detail'] as const,
};

/**
 * Fetch company info
 */
export function useCompanyInfo() {
  return useQuery({
    queryKey: companyInfoQueryKeys.detail(),
    queryFn: async () => {
      const response = await companyInfoApi.get();
      return response.data as CompanyInfoPublic;
    },
  });
}

/**
 * Update company info
 */
export function useUpdateCompanyInfo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateCompanyInfoRequest) => {
      const response = await companyInfoApi.update(data);
      return response.data as CompanyInfoPublic;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: companyInfoQueryKeys.all,
      });
    },
  });
}

/**
 * Upload company logo
 */
export function useUploadLogo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (file: File) => {
      const response = await companyInfoApi.uploadLogo(file);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: companyInfoQueryKeys.all,
      });
    },
  });
}

/**
 * Delete company logo
 */
export function useDeleteLogo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await companyInfoApi.deleteLogo();
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: companyInfoQueryKeys.all,
      });
    },
  });
}

/**
 * Get logo URL
 */
export function useLogoUrl() {
  return companyInfoApi.getLogoUrl();
}
